import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { AppState } from 'src/app/core/app.state';
import { UserDetailDialogComponent } from './user-detail-dialog/user-detail-dialog.component';
import { UserFormDialogComponent } from './user-form-dialog/user-form-dialog.component';
import { IPagination } from 'src/app/shared/models/page.model';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { CommonService } from 'src/app/shared/common.service';
import { MfaService } from '../../../services/mfa.service';
import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

import { IRole, IUser, IUserFormPayload, IUserUpdate } from '../../../models/settings.models';
import {
  DeleteUser,
  RestoreUser,
  RestoreUserSuccess,
  RestoreUserError,
  AddUser,
  AddUserError,
  AddUserSuccess,
  FetchAllRoles,
  FetchUsers,
  SetRolesLoader,
  SetUsersLoader,
  UpdateUser,
  UpdateUserError,
  UpdateUserSuccess,
} from '../../../store/settings.actions';

export type UserDeletedFilter = 'false' | 'true' | 'all';

// ─────────────────────────────────────────────────────────────────────────────
// UsersComponent
// ─────────────────────────────────────────────────────────────────────────────
// Users management page with search and role filtering, table, form dialog
// (create/edit), and detail dialog. Dispatches fetch actions on init and
// delegates CRUD to child dialogs and store actions.
// ─────────────────────────────────────────────────────────────────────────────

const USER_ROLE_COLORS = [
  '#6449db', '#dc2626', '#059669', '#0891b2', '#d97706',
  '#7c3aed', '#ec4899', '#0ea5e9', '#65a30d', '#1d4ed8',
];
const EMPTY_STATE_VALUE = '--';
const DEFAULT_ROLE_COLOR = '#94a3b8';
type SortDirection = 'asc' | 'desc';
type SortField = 'user' | 'role' | 'status' | 'last_activity';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {

  // ── Form controls ─────────────────────────────────────────────────────────
  readonly searchControl = new FormControl('');
  readonly roleFilterControl = new FormControl<number>(0);
  /** Active / deleted / all. A select so it matches the role filter beside it. */
  readonly deletedFilterControl = new FormControl<UserDeletedFilter>('false');

  // ── Store observables ─────────────────────────────────────────────────────
  readonly users$: Observable<IUser[]> = this.store.select(state => state.settings.users);
  readonly metadata$: Observable<IPagination | null> = this.store.select(state => state.settings.metadata);
  readonly roles$: Observable<IRole[]> = this.store.select(state => state.settings.allRoles);
  // Search is handled by the backend (see searchControl subscription in ngOnInit) so
  // it applies across the full filtered dataset, not just the currently loaded page.
  readonly filteredUsers$: Observable<IUser[]> = combineLatest([
    this.users$,
    this.roleFilterControl.valueChanges.pipe(startWith(0)),
  ]).pipe(
    map(([users, selectedRoleId]) => {
      return (users ?? [])
        .filter(user => !selectedRoleId || user.roles?.some(role => role.id === selectedRoleId));
    })
  );
  /**
   * Only super-admins get the active/deleted/all filter. Prefers `settings.me`
   * (the /me payload, always authoritative) and falls back to the login payload
   * in localStorage so the first paint is correct before /me resolves.
   */
  readonly isSuperAdmin$: Observable<boolean> = this.store
    .select(state => state.settings.me)
    .pipe(
      map(me => me ?? LocalStorageService.get('currentUser')?.user ?? null),
      map(user => !!user?.is_super_admin),
    );

  // ── Local state ───────────────────────────────────────────────────────────
  deletedFilter: UserDeletedFilter = 'false';
  selectedUser: IUser | null = null;
  formDialogUser: IUser | null = null;
  roleOptions: SelectOption[] = [];
  deletedFilterOptions: SelectOption[] = [];
  private allRoles: IRole[] = [];
  private formDialogRef: MatDialogRef<UserFormDialogComponent> | null = null;
  private detailDialogRef: MatDialogRef<UserDetailDialogComponent> | null = null;
  private pendingEnabledByUserId = new Map<number, boolean>();

  // ── Readonly refs ─────────────────────────────────────────────────────────
  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;
  readonly emptyStateValue = EMPTY_STATE_VALUE;
  readonly currentUserEmail: string | null = LocalStorageService.get('currentUser')?.user?.email ?? null;
  get sortField(): SortField | null {
    return this.sortField$.getValue();
  }
  get sortMode(): SortDirection {
    return this.sortMode$.getValue();
  }

  // ── Private state ─────────────────────────────────────────────────────────
  private readonly sortField$ = new BehaviorSubject<SortField | null>(null);
  private readonly sortMode$ = new BehaviorSubject<SortDirection>('asc');
  private readonly destroy$ = new Subject<void>();
  readonly sortedUsers$: Observable<IUser[]> = combineLatest([
    this.filteredUsers$,
    this.sortField$,
    this.sortMode$,
  ]).pipe(
    map(([users, sortField, sortMode]) => {
      if (!users?.length) return [];
      if (!sortField) return users;
      return this.sortUsers(users, sortField, sortMode);
    })
  );

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private actions$: Actions,
    private mfa: MfaService,
    private common: CommonService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.store.dispatch(SetUsersLoader({ isLoading: true }));
    this.store.dispatch(FetchUsers({ page: { size: DEFAULT_PAGE_SIZE, number: 1 }, enabled: 'all', deleted: this.deletedFilter }));
    this.store.dispatch(SetRolesLoader({ isLoading: true }));
    this.store.dispatch(FetchAllRoles());

    this.deletedFilterOptions = [
      { label: this.translateService.instant('common.active'), value: 'false' },
      { label: this.translateService.instant('common.deleted'), value: 'true' },
      { label: this.translateService.instant('common.all'), value: 'all' },
    ];

    this.deletedFilterControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(filter => this.setDeletedFilter(filter ?? 'false'));

    this.roles$.pipe(takeUntil(this.destroy$)).subscribe(roles => {
      this.allRoles = roles ?? [];
      this.roleOptions = [
        { label: this.translateService.instant('common.all'), value: 0 },
        ...(roles ?? []).map(role => ({ label: this.getRoleLabel(role.name), value: role.id })),
      ];
    });

    this.actions$.pipe(ofType(AddUserSuccess, UpdateUserSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.closeFormDialog();
    });

    this.actions$.pipe(ofType(AddUserError, UpdateUserError), takeUntil(this.destroy$)).subscribe(() => {
      this.formDialogRef?.componentInstance.setSubmitting(false);
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(term => {
      this.store.dispatch(SetUsersLoader({ isLoading: true }));
      this.store.dispatch(FetchUsers({
        page: { size: DEFAULT_PAGE_SIZE, number: 1 },
        enabled: 'all',
        deleted: this.deletedFilter,
        search: (term ?? '').trim(),
      }));
    });

    this.actions$.pipe(ofType(UpdateUserSuccess), takeUntil(this.destroy$)).subscribe(({ user }) => {
      if (!user?.id) return;
      if (!this.selectedUser?.id || this.selectedUser.id !== user.id) return;

      const pendingEnabled = this.pendingEnabledByUserId.get(user.id);
      this.pendingEnabledByUserId.delete(user.id);

      const nextSelectedUser = { ...this.selectedUser, ...user };
      if (typeof pendingEnabled === 'boolean') {
        nextSelectedUser.enabled = pendingEnabled;
      }

      this.setDetailDialogUser(nextSelectedUser);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Table selection ───────────────────────────────────────────────────────

  selectUser(user: IUser): void {
    if (this.selectedUser?.id === user.id) {
      this.closeDetailDialog();
      return;
    }

    this.selectedUser = user;
    this.openDetailDialog(user);
  }

  closeDetailDialog(): void {
    this.detailDialogRef?.close();
    this.detailDialogRef = null;
    this.selectedUser = null;
  }

  trackByUserId(_: number, user: IUser): number {
    return user.id!;
  }

  onSort(field: SortField): void {
    const currentSortField = this.sortField$.getValue();
    const currentSortMode = this.sortMode$.getValue();
    const nextSortField = field;
    const nextSortMode: SortDirection = currentSortField === field
      ? (currentSortMode === 'asc' ? 'desc' : 'asc')
      : 'asc';

    this.sortField$.next(nextSortField);
    this.sortMode$.next(nextSortMode);
  }

  // ── Form dialog (create / edit) ───────────────────────────────────────────

  openCreateDialog(): void {
    this.selectedUser = null;
    this.formDialogUser = null;
    this.openFormDialog();
  }

  openEditDialog(user: IUser, event?: Event): void {
    event?.stopPropagation();
    this.formDialogUser = user;
    this.openFormDialog();
  }

  onDetailDialogEdit(user: IUser): void {
    this.closeDetailDialog();
    this.openEditDialog(user);
  }

  closeFormDialog(): void {
    this.formDialogRef?.close();
    this.formDialogRef = null;
    this.formDialogUser = null;
  }

  get formDialogRoleOptions(): SelectOption[] {
    return this.roleOptions.filter(option => {
      const id = Number(option.value);
      if (id === 0) return false;
      return !this.allRoles.find(r => r.id === id)?.is_protected;
    });
  }

  get formDialogDefaultRoleId(): number | null {
    const filterId = this.roleFilterControl.value;
    if (filterId && this.formDialogRoleOptions.some(o => o.value === filterId)) {
      return filterId;
    }
    const first = this.formDialogRoleOptions[0];
    return first ? Number(first.value) : null;
  }

  onFormDialogSave(payload: IUserFormPayload): void {
    if (this.formDialogUser) {
      const update: IUserUpdate = {
        id: this.formDialogUser.id!,
        name: payload.name,
        email: payload.email,
        role_ids: [payload.roleId],
      };
      this.store.dispatch(UpdateUser({ id: update.id, user: update }));
    } else {
      this.store.dispatch(AddUser({
        user: { name: payload.name, email: payload.email, enabled: true, role_ids: [payload.roleId] },
      }));
    }
  }

  // ── User actions ──────────────────────────────────────────────────────────

  toggleEnabled(user: IUser, event?: Event): void {
    event?.stopPropagation();
    const nextEnabled = !user.enabled;
    this.pendingEnabledByUserId.set(user.id!, nextEnabled);
    this.store.dispatch(UpdateUser({ id: user.id!, user: { enabled: nextEnabled } }));
  }

  deleteUser(user: IUser, event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.store.dispatch(DeleteUser({ id: user.id! }));
        if (this.selectedUser?.id === user.id) this.closeDetailDialog();
      }
    });
  }

  restoreUser(user: IUser, event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.restore'),
      },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.store.dispatch(RestoreUser({ id: user.id! }));
      }
    });
  }

  /**
   * Strips every second factor from another user's account.
   *
   * A direct service call, not an NgRx action: nothing on this page reads the
   * result, and the whole MFA surface sits behind MfaService.
   */
  resetUserMfa(user: IUser, event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        // The copy says the sessions go too: the server bumps the target's
        // token epoch, which "reset MFA" does not suggest on its own.
        text: this.translateService.instant('settings_security.reset_confirm'),
        confirm_text: this.translateService.instant('settings_security.reset'),
      },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.mfa
        .resetUserMfa(user.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () =>
            this.common.showToast(
              this.translateService.instant('settings_security.reset_done'),
              'SUCCESS'
            ),
          // The HTTP error interceptor already toasts the failure.
          error: () => undefined,
        });
    });
  }

  setDeletedFilter(filter: UserDeletedFilter): void {
    this.deletedFilter = filter;
    this.store.dispatch(SetUsersLoader({ isLoading: true }));
    this.store.dispatch(FetchUsers({
      page: { size: DEFAULT_PAGE_SIZE, number: 1 },
      enabled: 'all',
      deleted: filter,
      search: (this.searchControl.value ?? '').trim(),
    }));
  }

  onPageChange(event: { number: number; size: number }): void {
    this.store.dispatch(SetUsersLoader({ isLoading: true }));
    this.store.dispatch(FetchUsers({
      page: { size: event.size, number: event.number },
      enabled: 'all',
      deleted: this.deletedFilter,
      search: (this.searchControl.value ?? '').trim(),
    }));
  }

  // ── Formatters ────────────────────────────────────────────────────────────

  getRoleColor(role: IRole | null): string {
    return role ? USER_ROLE_COLORS[role.id! % USER_ROLE_COLORS.length] : DEFAULT_ROLE_COLOR;
  }

  getImageSrc(base64: string): string {
    if (!base64) return '';
    const decoded = atob(base64);
    return decoded.trim().startsWith('<?xml')
      ? `data:image/svg+xml;base64,${base64}`
      : `data:image/*;base64,${base64}`;
  }

  getLastActivity(user: IUser): string {
    if (!user.last_activity) return EMPTY_STATE_VALUE;
    try {
      const date = new Date(user.last_activity);
      if (isNaN(date.getTime())) return EMPTY_STATE_VALUE;
      const d = String(date.getDate()).padStart(2, '0');
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${d}/${m}/${y} ${h}:${min}`;
    } catch {
      return EMPTY_STATE_VALUE;
    }
  }

  getUserRole(user: IUser): IRole | null {
    return user.roles?.[0] ?? null;
  }

  getRoleLabel(roleName: string): string {
    return roleName || EMPTY_STATE_VALUE;
  }

  getUserStatusLabel(isEnabled: boolean): string {
    return this.translateService.instant(isEnabled ? 'common.active' : 'common.inactive');
  }

  getUserToggleActionLabel(isEnabled: boolean): string {
    return this.translateService.instant(isEnabled ? 'common.deactivate' : 'common.activate');
  }

  private openFormDialog(): void {
    const pendingFormDialogUser = this.formDialogUser;
    this.closeDetailDialog();
    this.closeFormDialog();
    this.formDialogUser = pendingFormDialogUser;

    const ref = this.dialog.open(UserFormDialogComponent, {
      data: {
        user: pendingFormDialogUser,
        roleOptions: this.formDialogRoleOptions,
        defaultRoleId: this.formDialogDefaultRoleId,
      },
      panelClass: 'settings-user-form-dialog',
      disableClose: true,
    });
    this.formDialogRef = ref;

    ref.componentInstance.save
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(payload => this.onFormDialogSave(payload));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.formDialogRef === ref) {
          this.formDialogRef = null;
          this.formDialogUser = null;
        }
      });
  }

  private openDetailDialog(user: IUser): void {
    this.closeDetailDialog();

    const ref = this.dialog.open(UserDetailDialogComponent, {
      panelClass: 'settings-user-detail-dialog',
      disableClose: false,
    });
    this.detailDialogRef = ref;
    this.setDetailDialogUser(user);

    ref.componentInstance.editUser
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(targetUser => this.onDetailDialogEdit(targetUser));

    ref.componentInstance.toggleEnabled
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(targetUser => this.toggleEnabled(targetUser));

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.detailDialogRef === ref) {
          this.detailDialogRef = null;
          this.selectedUser = null;
        }
      });
  }

  private setDetailDialogUser(user: IUser): void {
    this.selectedUser = user;

    if (!this.detailDialogRef) return;
    this.detailDialogRef.componentInstance.setUser(user);
  }

  private sortUsers(users: IUser[], sortField: SortField, sortDirection: SortDirection): IUser[] {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    return [...users].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'user':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'role':
          aValue = this.getRoleLabel(this.getUserRole(a)?.name || '').toLowerCase();
          bValue = this.getRoleLabel(this.getUserRole(b)?.name || '').toLowerCase();
          break;
        case 'status':
          aValue = a.enabled ? 1 : 0;
          bValue = b.enabled ? 1 : 0;
          break;
        case 'last_activity':
          aValue = this.getLastActivityTimestamp(a.last_activity);
          bValue = this.getLastActivityTimestamp(b.last_activity);
          break;
        default:
          return 0;
      }

      const mainComparison = this.compareSortValues(aValue, bValue);
      if (mainComparison !== 0) return mainComparison * directionMultiplier;

      const userNameComparison = this.compareSortValues(
        a.name?.toLowerCase() || '',
        b.name?.toLowerCase() || ''
      );
      return userNameComparison;
    });
  }

  private getLastActivityTimestamp(lastActivity: string | undefined): number {
    if (!lastActivity) return 0;
    const timestamp = new Date(lastActivity).getTime();
    return isNaN(timestamp) ? 0 : timestamp;
  }

  private compareSortValues(aValue: string | number, bValue: string | number): number {
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  }
}
